import React, { useEffect, useMemo, useState } from 'react';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import {
  ImageTransformation, Price as PriceInterface, ProductDetails as ProductDetailsInterface, Variant, ProductService
} from '@services';
import classnames from 'classnames/bind';
import { getImageTransformationByProductType, getSuccessfullyLoadedUrls } from '@utils';
import { useServiceRef } from 'hooks';
import styles from './ProductDetails.module.scss';
import {
  ShowMore,
  ProductOptions,
  ProductDetail,
  AddToCartButton,
  Price,
  Button,
  InStoreAvailabilityModal,
  Placement,
  ARPreview
} from '@components';
import {
  Carousel,
  Typography,
  Counter,
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionHeader,
  Icon,
  Rating
} from 'gd-design-library';
import NoImagePlaceholder from '@assets/images/no-image-placeholder.png';
import { ProductDetailsSkeleton } from './ProductDetails.skeleton';
import { InStoreAvailabilityItem } from '@services/cart';
import { ProductEnrichmentDetails } from '../ProductEnrichmentDetails/ProductEnrichmentDetails';
import { ProductDetailsReviews } from '../ProductDetailsReviews/ProductDetailsReviews';
import { GoogleAIPrompts } from '../GoogleAIPrompts';

const CN = 'product-details';
const galleryClassName = `${CN}__gallery`;
const galleryButtonsClassName = `${CN}__gallery-buttons`;
const infoClassName = `${CN}__info`;
const nameContainerClassName = `${CN}__name-container`;
const ratingClassName = `${CN}__rating`;
const nameClassName = `${CN}__name`;
const priceClassName = `${CN}__price`;
const buttonClassName = `${CN}__button`;
const findStoreButtonClassName = `${CN}__find-store-button`;
const cx = classnames.bind(styles);

const isCloudinaryUrl = (url?: string): boolean => !!url?.includes('cloudinary');

interface ProductDetailsProps {
  product: ProductDetailsInterface;
  setSelectedVariant: (variant: Variant) => void;
  selectedVariant: Variant;
  isLoading?: boolean;
  onAddToCart: () => void;
  quantityValue: number;
  setQuantityValue: React.Dispatch<React.SetStateAction<number>>;
  id: string | string[] | undefined;
  isAddingToCart: boolean;
  inStoreAvailabilityData: InStoreAvailabilityItem[];
  getInStoreAvailabilityData: () => Promise<void>;
}

type Availability = Record<string, { values: Record<string, boolean> }>

export const ProductDetails: React.FC<ProductDetailsProps> = ({
  product: {
    images,
    assets,
    name,
    price,
    description,
    options,
    variants,
    details,
    productType,
    isVirtual,
    enrichmentDetails,
    sizeGuide,
    reviewSummary,
    reviews
  },
  id,
  selectedVariant,
  setSelectedVariant,
  quantityValue,
  setQuantityValue,
  isLoading,
  onAddToCart,
  isAddingToCart,
  inStoreAvailabilityData,
  getInStoreAvailabilityData
}) => {
  const { attributes: selectedVariantAttributes, images: selectedVariantImages, price: selectedVariantPrice } = selectedVariant;
  const currentImages: string[] = useMemo(
    () => selectedVariantImages?.length ? selectedVariantImages : images, [ selectedVariantImages, images ]
  );
  const currentPrice: PriceInterface = selectedVariantPrice?.value ? selectedVariantPrice : price;
  const [ successfullyLoadedImages, setSuccessfullyLoadedImages ] = useState<string[]>([]);
  const [ isLoadingImages, setIsLoadingImages ] = useState(true);
  const [ isInStoreAvailabilityModalOpen, setIsInStoreAvailabilityModalOpen ] = useState(false);
  const [ isInStoreAvailabilityDataLoading, setIsInStoreAvailabilityDataLoading ] = useState(false);
  const [ availableQuantity, setAvailableQuantity ] = useState<number | null>(null);

  const [ optionsAvailability, setOptionsAvailability ] = useState<Availability>({});
  const productServiceRef = useServiceRef(ProductService);

  const imageTransformation = new ImageTransformation();

  const productImageURLs = successfullyLoadedImages.length ?
    successfullyLoadedImages.map(image => isCloudinaryUrl(image)
      ? imageTransformation.transform(
        image,
        getImageTransformationByProductType(productType)
      )
      : image) :
    [ NoImagePlaceholder.src ];
  const glbAsset = assets.find(asset => asset.sources.some(source => source.contentType === 'model/gltf-binary'));

  const outOfStockOptions = useMemo(() => {
    if (!options || !variants || !selectedVariantAttributes) return {};

    const result: Record<string, Record<string, boolean>> = {};

    for (const option of options) {
      result[option.name] = {};

      if (option.values) {
        for (const value of option.values) {
          const attributes = { ...selectedVariantAttributes, [option.name]: value };
          const variant = variants.find(v =>
            Object.entries(attributes).every(([ k, vAttr ]) => v.attributes[k] === vAttr)
          );
          result[option.name][String(value)] = true;
          if (variant && variant.isOnStock) {
            result[option.name][String(value)] = false;
          }
        }
      }
    }

    return result;
  }, [ options, variants, selectedVariantAttributes ]);

  useEffect(() => {
    getSuccessfullyLoadedUrls(currentImages).then((successfullyLoadedUrls: string[]) => {
      if (successfullyLoadedUrls.length) {
        setSuccessfullyLoadedImages(successfullyLoadedUrls);
      }
      setIsLoadingImages(false);
    });
  }, [ currentImages ]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (selectedVariant?.sku) {
        const quantity = await productServiceRef.current.getAvailableQuantity(selectedVariant.sku);
        setAvailableQuantity(quantity);
      } else {
        setAvailableQuantity(null);
      }
    })();
  }, [ selectedVariant?.sku ]);


  const onClickOption = (optionName: string, optionValue: unknown): void => {
    const userSelectedVariant = { ...selectedVariantAttributes, [optionName]: optionValue };
    const foundVariant = variants.find(variant => JSON.stringify(variant.attributes) === JSON.stringify(userSelectedVariant));
    if (foundVariant) {
      setSelectedVariant({ ...foundVariant });
    } else {
      const anotherVariant = variants.find(variant => variant.attributes[optionName] === optionValue);
      if (anotherVariant) {
        setSelectedVariant(anotherVariant);
      }
    }
  };

  useEffect(() => {
    const variantSet = variants.reduce((acc, variant) => acc.add(JSON.stringify(variant.attributes)), new Set());
    const calculatedOptionsAvailability: Availability = {};

    options.forEach(option => {
      calculatedOptionsAvailability[option.name] = { values: {} };

      option.values?.forEach(value => {
        calculatedOptionsAvailability[option.name].values[String(value)] = false;
        const newVariant = { ...selectedVariantAttributes };
        newVariant[option.name] = value;

        if (variantSet.has(JSON.stringify(newVariant))) {
          calculatedOptionsAvailability[option.name].values[String(value)] = true;
        }
      });
    });

    setOptionsAvailability(calculatedOptionsAvailability);
  }, [ selectedVariantAttributes, variants, options ]);

  const handleFindStoresClick = async (): Promise<void> => {
    setIsInStoreAvailabilityDataLoading(true);
    await getInStoreAvailabilityData();
    setIsInStoreAvailabilityDataLoading(false);
    setIsInStoreAvailabilityModalOpen(true);
  };

  return !isLoading ? (
    <>
      <div className={ cx(CN) }>
        <div className={ cx(galleryClassName) }>
          {!isLoadingImages && (
            productImageURLs.length === 1 ?
              <img src={ productImageURLs[0] } alt={ name } style={ { width: '100%' } } /> :
              <Carousel showArrows>
                {productImageURLs.map(src => <Carousel.Image key={ src } src={ src } />)}
              </Carousel>
          )}
          <div className={ cx(galleryButtonsClassName) }>
            {glbAsset && isFeatureEnabled(FEATURES.AR_PREVIEW) && <ARPreview modelSrc={ glbAsset.sources[0].uri } />}
            {
              isFeatureEnabled(FEATURES.PLACEMENT) &&
            typeof id === 'string' &&
            images.length > 0 &&
            <Placement productId={ id } />
            }
          </div>
        </div>
        <div className={ cx(infoClassName) }>
          <div className={ cx(nameContainerClassName) }>
            <Typography variant='h5' className={ cx(nameClassName) }>{name}</Typography>
            {reviewSummary && (
              <button
                className={ cx(ratingClassName) }
                onClick={ (): void => {
                  const reviewsElement = document.getElementById('reviews');
                  if (reviewsElement) {
                    reviewsElement.scrollIntoView({ behavior: 'smooth' });
                  }
                } }
              >
                <Rating value={ reviewSummary?.averageRating } readOnly size='sm' />
                <Typography variant='caption' as='span'>
                  {reviewSummary?.averageRating.toFixed(1)} ({reviewSummary?.reviewCount} ratings & reviews)
                </Typography>
              </button>
            )}
          </div>
          <hr />
          <Price price={ selectedVariant ? selectedVariant.price : currentPrice } className={ cx(priceClassName) } />
          <hr />
          {description && <><ShowMore>{description}</ShowMore> <hr /> </>}
          { isFeatureEnabled(FEATURES.GOOGLE_SHOPPING_AGENT) && (
            <>
              <GoogleAIPrompts />
              <hr />
            </>
          )}
          {options
            // TODO: Remove this once the option is removed from the products or sokos tenant is removed
            .filter(option => option.name !== 'sokos-sokos_product-purchase_limit')
            .map(currentOptions => (
              <React.Fragment key={ currentOptions.name }>
                <div>
                  <ProductOptions
                    options={ currentOptions }
                    selectedOption={ selectedVariantAttributes?.[currentOptions.name] }
                    availability={ optionsAvailability[currentOptions.name]?.values }
                    outOfStockOptions={ outOfStockOptions[currentOptions.name] }
                    onClickOption={ onClickOption }
                    variants={ variants }
                  />
                </div>
                <hr />
              </React.Fragment>
            ))}
          <div style={ { padding: '0' } }>
            <ProductEnrichmentDetails enrichmentDetails={ enrichmentDetails } sizeGuide={ sizeGuide } />
          </div>
          {!!details.length &&
            <Accordion>
              <AccordionItem key='other-properties' id='other-properties' style={ { padding: '0' } }>
                <AccordionHeader
                  expandIcon={ <Icon name='keyboardArrowDown' size='xs' /> }
                  style={ { margin: '0.5rem 0' } }
                >
                  Other Properties
                </AccordionHeader>
                <AccordionContent>
                  <div style={ { padding: '0 1rem 0.5rem 1rem' } }>
                    {details.map(detail => <ProductDetail key={ detail.name } attribute={ detail } />)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          }
          <Counter
            initial={ quantityValue }
            min={ 1 }
            max={ availableQuantity ?? (!selectedVariant.isOnStock ? 1 : undefined) }
            isDisabled={ !selectedVariant.isOnStock }
            onCounterChange={ setQuantityValue }
          />
          <AddToCartButton
            className={ buttonClassName }
            onClick={ onAddToCart }
            isLoading={ isAddingToCart }
            disabled={ selectedVariant?.price?.value === undefined || currentPrice?.value === undefined }
            isOnStock={ selectedVariant.isOnStock }
          />
          {!isVirtual ? (
            <>
              <Typography style={ { margin: '1.5rem 0 1rem 0' } }>Search for products availability at stores.</Typography>
              <Button
                loading={ isInStoreAvailabilityDataLoading }
                variant='outlined'
                className={ cx(findStoreButtonClassName) }
                onClick={ handleFindStoresClick }
              >
                  Find a Store
              </Button>
            </>
          ) : null}
        </div>
        <InStoreAvailabilityModal
          isOpen={ isInStoreAvailabilityModalOpen }
          onClose={ (): unknown => setIsInStoreAvailabilityModalOpen(false) }
          stores={ inStoreAvailabilityData }
        />
      </div>
      <ProductDetailsReviews reviewSummary={ reviewSummary } reviews={ reviews } productId={ typeof id === 'string' ? id : '' } />
    </>
  ) : <ProductDetailsSkeleton />;
};
